/**
 * Analytics Provider for AI Telemetry Dashboard
 * Provides comprehensive analytics functionality built on PostHog
 * Integrates with existing error monitoring service
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { errorMonitoringService } from '../lib/monitoring/error-monitoring';

export interface AnalyticsEvent {
  [key: string]: string | number | boolean | Date | undefined | null;
}

export interface UserProperties {
  userId?: string;
  organizationId?: string;
  role?: string;
  email?: string;
  plan?: string;
  environment?: string;
  [key: string]: any;
}

interface AnalyticsContextType {
  // Core tracking methods
  trackEvent: (eventName: string, properties?: AnalyticsEvent) => void;
  trackPageView: (pageName: string, properties?: AnalyticsEvent) => void;
  trackUserAction: (action: string, component: string, properties?: AnalyticsEvent) => void;

  // Dashboard-specific tracking
  trackDashboardView: (dashboardType: string, properties?: AnalyticsEvent) => void;
  trackDataQuery: (queryType: string, properties?: AnalyticsEvent) => void;
  trackExperimentAction: (action: string, properties?: AnalyticsEvent) => void;
  trackDeploymentAction: (action: string, properties?: AnalyticsEvent) => void;
  trackIntegrationUsage: (integration: string, action: string, properties?: AnalyticsEvent) => void;

  // Performance tracking
  trackPerformanceMetric: (metricName: string, value: number, unit: string, properties?: AnalyticsEvent) => void;
  trackLoadTime: (component: string, loadTimeMs: number) => void;
  trackError: (error: Error, context?: string, properties?: AnalyticsEvent) => void;

  // User management
  identifyUser: (userId: string, properties?: UserProperties) => void;
  updateUserProperties: (properties: UserProperties) => void;
  resetUser: () => void;

  // Feature adoption tracking
  trackFeatureUsage: (featureName: string, properties?: AnalyticsEvent) => void;
  trackFeatureDiscovery: (featureName: string, discoveryMethod: string) => void;

  // Business metrics
  trackBusinessMetric: (metricName: string, value: number, properties?: AnalyticsEvent) => void;
  trackConversion: (conversionType: string, properties?: AnalyticsEvent) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  // Check if analytics is enabled
  const isAnalyticsEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
  const isUserAnalyticsEnabled = import.meta.env.VITE_ENABLE_USER_ANALYTICS === 'true';
  const isPerformanceTrackingEnabled = import.meta.env.VITE_ENABLE_PERFORMANCE_TRACKING === 'true';
  const sampleRate = parseFloat(import.meta.env.VITE_ANALYTICS_SAMPLE_RATE || '1.0');

  useEffect(() => {
    // Initialize error monitoring service (which includes PostHog)
    if (isAnalyticsEnabled) {
      errorMonitoringService.initialize().catch(error => {
        console.warn('Failed to initialize analytics:', error);
      });
    }
  }, [isAnalyticsEnabled]);

  // Helper function to check if event should be tracked based on sampling
  const shouldTrackEvent = (): boolean => {
    return isAnalyticsEnabled && Math.random() < sampleRate;
  };

  // Core tracking methods
  const trackEvent = (eventName: string, properties: AnalyticsEvent = {}) => {
    if (!shouldTrackEvent()) return;

    const enhancedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      environment: import.meta.env.VITE_ENV || 'development',
      app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
    };

    errorMonitoringService.trackUserEvent({
      name: eventName,
      properties: enhancedProperties,
      timestamp: new Date(),
    });
  };

  const trackPageView = (pageName: string, properties: AnalyticsEvent = {}) => {
    trackEvent('page_viewed', {
      page_name: pageName,
      page_title: document.title,
      ...properties,
    });
  };

  const trackUserAction = (action: string, component: string, properties: AnalyticsEvent = {}) => {
    trackEvent('user_action', {
      action,
      component,
      ...properties,
    });
  };

  // Dashboard-specific tracking
  const trackDashboardView = (dashboardType: string, properties: AnalyticsEvent = {}) => {
    trackEvent('dashboard_viewed', {
      dashboard_type: dashboardType,
      ...properties,
    });
  };

  const trackDataQuery = (queryType: string, properties: AnalyticsEvent = {}) => {
    trackEvent('data_query_executed', {
      query_type: queryType,
      ...properties,
    });
  };

  const trackExperimentAction = (action: string, properties: AnalyticsEvent = {}) => {
    trackEvent('experiment_action', {
      action,
      ...properties,
    });
  };

  const trackDeploymentAction = (action: string, properties: AnalyticsEvent = {}) => {
    trackEvent('deployment_action', {
      action,
      ...properties,
    });
  };

  const trackIntegrationUsage = (integration: string, action: string, properties: AnalyticsEvent = {}) => {
    trackEvent('integration_used', {
      integration,
      action,
      ...properties,
    });
  };

  // Performance tracking
  const trackPerformanceMetric = (metricName: string, value: number, unit: string, properties: AnalyticsEvent = {}) => {
    if (!isPerformanceTrackingEnabled) return;

    errorMonitoringService.reportPerformanceMetric({
      name: metricName,
      value,
      unit,
      timestamp: new Date(),
      context: properties,
    });
  };

  const trackLoadTime = (component: string, loadTimeMs: number) => {
    trackPerformanceMetric('component_load_time', loadTimeMs, 'ms', {
      component,
    });
  };

  const trackError = (error: Error, context: string = '', properties: AnalyticsEvent = {}) => {
    errorMonitoringService.reportError(error, {
      userId: (properties.userId as string) || undefined,
      organizationId: (properties.organizationId as string) || undefined,
      url: window.location.href,
      timestamp: new Date(),
    });
  };

  // User management
  const identifyUser = (userId: string, properties: UserProperties = {}) => {
    if (!isUserAnalyticsEnabled) return;

    errorMonitoringService.setUserContext(userId, {
      ...properties,
      identified_at: new Date().toISOString(),
    });
  };

  const updateUserProperties = (properties: UserProperties) => {
    if (!isUserAnalyticsEnabled) return;

    // Update user properties through PostHog
    if (window.posthog) {
      window.posthog.setPersonProperties(properties);
    }
  };

  const resetUser = () => {
    if (!isUserAnalyticsEnabled) return;

    errorMonitoringService.clearUserContext();
  };

  // Feature adoption tracking
  const trackFeatureUsage = (featureName: string, properties: AnalyticsEvent = {}) => {
    trackEvent('feature_used', {
      feature_name: featureName,
      ...properties,
    });
  };

  const trackFeatureDiscovery = (featureName: string, discoveryMethod: string) => {
    trackEvent('feature_discovered', {
      feature_name: featureName,
      discovery_method: discoveryMethod,
    });
  };

  // Business metrics
  const trackBusinessMetric = (metricName: string, value: number, properties: AnalyticsEvent = {}) => {
    trackEvent('business_metric', {
      metric_name: metricName,
      metric_value: value,
      ...properties,
    });
  };

  const trackConversion = (conversionType: string, properties: AnalyticsEvent = {}) => {
    trackEvent('conversion', {
      conversion_type: conversionType,
      ...properties,
    });
  };

  const contextValue: AnalyticsContextType = {
    trackEvent,
    trackPageView,
    trackUserAction,
    trackDashboardView,
    trackDataQuery,
    trackExperimentAction,
    trackDeploymentAction,
    trackIntegrationUsage,
    trackPerformanceMetric,
    trackLoadTime,
    trackError,
    identifyUser,
    updateUserProperties,
    resetUser,
    trackFeatureUsage,
    trackFeatureDiscovery,
    trackBusinessMetric,
    trackConversion,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}

// Export types for use in other components
export type { AnalyticsEvent, UserProperties };